import { Fragment, HTMLProps, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, SortingState, useReactTable, ExpandedState } from "@tanstack/react-table";

import { CategoryWithParts, ILeadPartsTableGlobalFilters, IPart, PhysicalRouting } from "../../../../models/Part";
import { ChevronRightIcon, EllipsisVerticalIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import classNames from "../../../../utils/classNames";
import LeadPartsCategoriesTableSubRow from "./LeadPartsCategoriesTableSubRow";
import { DateTime } from "luxon";
import LoadingPulse from "../../../shared/LoadingPulse";
import { LastSoldPriceButton, SmartPriceButton, PartProPriceButton } from "../../../shared/PriceButtons";
import formatDateTime from "../../../../utils/formatDateTime";
import EbayLinkButton from "../../../shared/Inputs/EbayLinkButton";
import { IVehicle } from "../../../../models/Vehicle";
import useSingleSetting from "../../../../data/useSingleSetting";
import RsPriceButton from "../../../shared/PriceButtons/RsPriceButton";
import PartDiagramModal, { DiagramMode } from "../../../GtMotiveWidgets/PartDiagramModal";
import PriceFormat from "../../../../utils/priceFormat";
import useVehiclePricingMatrix from "../../../../data/useVehiclePricingMatrix";
import Modal from "../../../../layouts/Modal";
import { XMarkIcon as XMarkSolidIcon } from "@heroicons/react/24/solid";
import useFlowTypeAccounts from "../../../../data/useFlowTypeAccounts";
import { CheckFeatureFlag } from "../../../../hooks/useFeatureFlag";

const colHelper = createColumnHelper<CategoryWithParts>();
const accountTypeLabelMap: Record<number, string> = {
  5: "Reuse",
  6: "Remanufacturing",
  7: "Reintegration",
  8: "Recycling",
};
const accountTypePillClassMap: Record<number, string> = {
  5: "border-emerald-200 bg-emerald-50 text-emerald-700",
  6: "border-orange-200 bg-orange-50 text-orange-700",
  7: "border-blue-200 bg-blue-50 text-blue-700",
  8: "border-purple-200 bg-purple-50 text-purple-700",
};

interface IBestPriceOption {
  accountId: string;
  accountName: string;
  accountType: number;
  price: number;
  physicalRoutingDefault: PhysicalRouting;
}

interface IAccountSelectionPayload {
  accountId: string | null;
  accountName: string | null;
  accountType: number | null;
  physicalRoutingDefault?: PhysicalRouting | null;
}

const physicalRoutingOptions: { value: PhysicalRouting; label: string }[] = [
  { value: PhysicalRouting.None, label: "None" },
  { value: PhysicalRouting.Bins, label: "Bins" },
  { value: PhysicalRouting.Staging, label: "Staging" },
  { value: PhysicalRouting.Pickup, label: "Pickup" },
];

// What the auto-select effect last applied to a row, so we can re-evaluate
// (e.g. upgrade to RS once RS prices load) without clobbering manual edits.
type AutoSelectIntent =
  | { kind: "rs"; price: number }
  | { kind: "matrix"; price: number; accountId: string }
  | { kind: "recycling"; accountId: string };

function IndeterminateCheckbox({
  indeterminate,
  className = "",
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + "p-3 cursor-pointer"}
      {...rest}
    />
  );
}


function EditablePriceCell({
  rowId,
  value,
  accountId,
  accountName,
  accountType,
  onClearAccount,
  handleSetTableData
}: {
  rowId: string;
  value: number;
  accountId?: string | null;
  accountName?: string | null;
  accountType?: number | null;
  onClearAccount: (rowId: string) => void;
  handleSetTableData: (
    rowId: string,
    price: number | undefined,
    note?: string | undefined,
    isCore?: boolean | undefined,
    accountSelection?: IAccountSelectionPayload | undefined,
    clearSelectedAccount?: boolean
  ) => void;
}) {
  const [localPriceValue, setLocalPriceValue] = useState<number>(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const commitTimerRef = useRef<number | undefined>(undefined);

  // Whenever the parent value changes (e.g. clicking the Suggested Price button),
  // update localValue so the input shows the latest.
  useEffect(() => {
    // Avoid clobbering the in-progress value while this field has focus.
    if (document.activeElement === inputRef.current) return;
    setLocalPriceValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (typeof commitTimerRef.current !== "undefined") {
        window.clearTimeout(commitTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="flex items-center gap-2">
      <div className="relative w-24 shrink-0">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">£</span>
        <input
          ref={inputRef}
          type="number"
          value={localPriceValue}
          min={0}
          onChange={(e) => {
            // If user clears the field, treat as 0
            const raw = e.target.value;
            const parsed = raw === "" ? 0 : parseFloat(raw);
            setLocalPriceValue(Number.isNaN(parsed) ? 0 : parsed);
          }}
          onBlur={() => {
            // Defer commit so focus transition to the next field can complete first.
            if (localPriceValue !== value) {
              const valueToCommit = localPriceValue;
              if (typeof commitTimerRef.current !== "undefined") {
                window.clearTimeout(commitTimerRef.current);
              }
              commitTimerRef.current = window.setTimeout(() => {
                handleSetTableData(rowId, valueToCommit);
              }, 0);
            }
          }}
          className="border p-1 pl-5 w-full"
        />
      </div>
      {!!accountId && (
        <div className="flex items-center gap-1">
          <span
            className={classNames(
              "max-w-[145px] truncate rounded-sm border px-1.5 py-0.5 text-[10px] font-normal leading-tight",
              typeof accountType === "number"
                ? accountTypePillClassMap[accountType]
                : "border-gray-200 bg-gray-100 text-gray-600"
            )}
            title={accountName ?? accountId}
          >
            {accountName ?? accountId}
          </span>
          <button
            type="button"
            onClick={() => onClearAccount(rowId)}
            className="rounded border border-gray-300 p-0.5 text-gray-500 hover:bg-gray-100"
            title="Remove selected account"
          >
            <XMarkSolidIcon className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function EditableNoteCell({ rowId, value, handleSetTableData }: { rowId: string; value: string; handleSetTableData: (rowId: string, price: number | undefined, note?: string | undefined) => void; }) {
  const [localNoteValue, setLocalNoteValue] = useState<string>(value);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const commitTimerRef = useRef<number | undefined>(undefined);

  // Keep local state in sync if parent value changes (e.g., external updates)
  useEffect(() => {
    // Avoid clobbering the in-progress value while this field has focus.
    if (document.activeElement === textAreaRef.current) return;
    setLocalNoteValue(value ?? "");
  }, [value]);

  useEffect(() => {
    return () => {
      if (typeof commitTimerRef.current !== "undefined") {
        window.clearTimeout(commitTimerRef.current);
      }
    };
  }, []);

  return (
    <textarea
      ref={textAreaRef}
      value={localNoteValue}
      rows={1}
      onChange={(e) => {
        setLocalNoteValue(e.target.value);
      }}
      onBlur={() => {
        // Defer commit so focus transition to the next field can complete first.
        if (localNoteValue !== value) {
          const valueToCommit = localNoteValue;
          if (typeof commitTimerRef.current !== "undefined") {
            window.clearTimeout(commitTimerRef.current);
          }
          commitTimerRef.current = window.setTimeout(() => {
            handleSetTableData(rowId, undefined, valueToCommit);
          }, 0);
        }
      }}
      className="border p-1 text-sm w-full"
    />
  );
}

export interface LeadPartsCategoriesTableProps {
  categories: CategoryWithParts[];
  selectedCategories?: CategoryWithParts[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<CategoryWithParts[]>>;
  partProUnlocked: boolean;
  partProLoading: boolean;
  vehicle: IVehicle;
  filter?: (cat: CategoryWithParts) => boolean;
  leadPartsTableFilters: ILeadPartsTableGlobalFilters;
  showBestPrice?: boolean;
  autoSelect?: boolean;
}

export default function LeadPartsCategoriesTable({
  categories,
  selectedCategories,
  setSelectedCategories,
  partProUnlocked,
  partProLoading,
  vehicle,
  filter,
  leadPartsTableFilters,
  showBestPrice = false,
  autoSelect = false,
}: LeadPartsCategoriesTableProps) {
  const hasTmeFeature = CheckFeatureFlag("TME");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [tableData, setTableData] = useState<CategoryWithParts[]>(categories);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { singleSetting } = useSingleSetting("Rs:ApiKey");
  const [hasRsKey, setHasRsKey] = useState(false);
  const [diagramOpen, setDiagramOpen] = useState(false);
  const [diagramMode, setDiagramMode] = useState<DiagramMode>({ type: "findAll" });
  const [diagramCategory, setDiagramCategory] = useState<CategoryWithParts["category"] | null>(null);
  const [priceOptionsModalOpen, setPriceOptionsModalOpen] = useState(false);
  const [selectedPriceOptions, setSelectedPriceOptions] = useState<{
    categoryId: string;
    categoryName: string;
    bestOption: IBestPriceOption;
    otherOptions: IBestPriceOption[];
  } | null>(null);
  const setSelectedCategoriesRef = useRef(setSelectedCategories);
  const tableDataRef = useRef(tableData);
  const { vehiclePricingMatrix } = useVehiclePricingMatrix(vehicle?.id, "LeadPartsCategoriesTable");
  const { flowTypeAccounts } = useFlowTypeAccounts("LeadPartsCategoriesTable");

  useEffect(() => {
    setSelectedCategoriesRef.current = setSelectedCategories;
  }, [setSelectedCategories]);

  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  const filteredTableData = useMemo(() => {
    return filter ? tableData.filter(filter) : tableData;
  }, [tableData, filter]);

  const rowSelection = useMemo(() => {
    const sel: Record<string, boolean> = {};
    selectedCategories?.forEach((cat) => {
      sel[cat.category.id] = true;
    });
    return sel;
  }, [selectedCategories]);

  const handleSetTableData = useCallback((
    rowId: string,
    newPrice: number | undefined,
    newNote: string | undefined = undefined,
    isCore: boolean | undefined = undefined, // Is refurbished / Rs
    accountSelection: IAccountSelectionPayload | undefined = undefined,
    clearSelectedAccount: boolean = false,
    physicalRouting: PhysicalRouting | undefined = undefined
  ) => {
    const currentTableData = tableDataRef.current;
    const existingRow = currentTableData.find((item) => item.category.id === rowId);
    if (!existingRow) return;

    const hasNumericPrice = typeof newPrice === "number" && !Number.isNaN(newPrice);
    const numericPrice = hasNumericPrice ? newPrice : undefined;
    const shouldUpdateNote = typeof newNote !== "undefined";
    const shouldUpdateCore = typeof isCore === "boolean";
    const shouldUpdateAccount = typeof accountSelection !== "undefined" || clearSelectedAccount;
    const shouldUpdatePhysicalRouting = typeof physicalRouting !== "undefined";
    const shouldClearAccountForPriceReset = hasNumericPrice && numericPrice! <= 0;

    const nextPrice = hasNumericPrice ? numericPrice : existingRow.price;
    const nextNote = shouldUpdateNote ? newNote : existingRow.note;
    const nextIsCore = shouldUpdateCore ? isCore : existingRow.isCore;
    const nextAccountId = shouldClearAccountForPriceReset || clearSelectedAccount
      ? null
      : shouldUpdateAccount
        ? accountSelection?.accountId ?? null
        : existingRow.accountId ?? null;
    const nextAccountName = shouldClearAccountForPriceReset || clearSelectedAccount
      ? null
      : shouldUpdateAccount
        ? accountSelection?.accountName ?? null
        : existingRow.accountName ?? null;
    const nextAccountType = shouldClearAccountForPriceReset || clearSelectedAccount
      ? null
      : shouldUpdateAccount
        ? accountSelection?.accountType ?? null
        : existingRow.accountType ?? null;
    // Routing follows the account: clearing the account resets it, selecting an
    // account defaults it to that account's PhysicalRoutingDefault, and an explicit
    // dropdown change overrides everything.
    const nextPhysicalRouting = shouldClearAccountForPriceReset || clearSelectedAccount
      ? PhysicalRouting.None
      : shouldUpdatePhysicalRouting
        ? physicalRouting
        : shouldUpdateAccount
          ? accountSelection?.physicalRoutingDefault ?? PhysicalRouting.None
          : existingRow.physicalRouting ?? PhysicalRouting.None;

    const updatedRow: CategoryWithParts = {
      ...existingRow,
      price: nextPrice,
      note: nextNote,
      isCore: nextIsCore,
      accountId: nextAccountId,
      accountName: nextAccountName,
      accountType: nextAccountType,
      physicalRouting: nextPhysicalRouting,
    };

    const nextSelectedUpdater = (prevSelectedForThisTable: CategoryWithParts[]) => {
      const selectedIndex = prevSelectedForThisTable.findIndex(
        (cat) => cat.category.id === rowId
      );
      const isCurrentlySelected = selectedIndex !== -1;

      if (hasNumericPrice) {
        if (numericPrice! <= 0) {
          if (!isCurrentlySelected) return prevSelectedForThisTable;
          return prevSelectedForThisTable.filter((cat) => cat.category.id !== rowId);
        }

        if (!isCurrentlySelected) {
          return [...prevSelectedForThisTable, updatedRow];
        }

        const nextSelected = [...prevSelectedForThisTable];
        nextSelected[selectedIndex] = updatedRow;
        return nextSelected;
      }

      if (shouldUpdateNote && isCurrentlySelected) {
        const nextSelected = [...prevSelectedForThisTable];
        nextSelected[selectedIndex] = updatedRow;
        return nextSelected;
      }

      if (shouldUpdateAccount || shouldUpdateCore || shouldUpdatePhysicalRouting) {
        if (!isCurrentlySelected) {
          return [...prevSelectedForThisTable, updatedRow];
        }

        const nextSelected = [...prevSelectedForThisTable];
        nextSelected[selectedIndex] = updatedRow;
        return nextSelected;
      }

      return prevSelectedForThisTable;
    };

    setSelectedCategoriesRef.current(nextSelectedUpdater);

    const didChange =
      nextPrice !== existingRow.price ||
      nextNote !== existingRow.note ||
      nextIsCore !== existingRow.isCore ||
      nextAccountId !== existingRow.accountId ||
      nextAccountName !== existingRow.accountName ||
      nextAccountType !== existingRow.accountType ||
      nextPhysicalRouting !== (existingRow.physicalRouting ?? PhysicalRouting.None);

    if (!didChange) return;

    setTableData((old) => old.map((item) => (
      item.category.id === rowId ? updatedRow : item
    )));
  }, []);

  const makeModelString = useMemo(() => {
    let string = "";

    if (vehicle?.dvlaYearOfManufacture) {
      string = vehicle?.dvlaYearOfManufacture + "+";
    }

    string += vehicle?.combinedMake;
    string += "+";
    string += vehicle?.modelRangeDescription;

    return string;
  }, [vehicle])

  const bestMatrixPriceByCategoryId = useMemo(() => {
    const matrix = vehiclePricingMatrix.data?.data;
    if (!matrix) {
      return new Map<string, { bestOption: IBestPriceOption; options: IBestPriceOption[] }>();
    }

    const accountById = new Map(
      matrix.accounts.map((account) => [
        account.accountId,
        {
          accountName: account.accountName,
          accountType: account.accountType,
          physicalRoutingDefault: account.physicalRoutingDefault ?? PhysicalRouting.None,
        },
      ])
    );

    const bestByCategory = new Map<string, { bestOption: IBestPriceOption; options: IBestPriceOption[] }>();

    matrix.categories.forEach((category) => {
      const options: IBestPriceOption[] = [];

      category.cells.forEach((cell) => {
        if (!cell.enabled || cell.price == null) {
          return;
        }

        const account = accountById.get(cell.accountId);
        if (!account) {
          return;
        }

        options.push({
          accountId: cell.accountId,
          price: cell.price,
          accountName: account.accountName,
          accountType: account.accountType,
          physicalRoutingDefault: account.physicalRoutingDefault,
        });
      });

      if (!options.length) {
        return;
      }

      const sortedOptions = [...options].sort((a, b) => b.price - a.price);
      bestByCategory.set(category.partCategoryId, {
        bestOption: sortedOptions[0],
        options: sortedOptions,
      });
    });

    return bestByCategory;
  }, [vehiclePricingMatrix.data?.data]);

  const firstRecyclingAccount = useMemo(() => {
    const recyclingAccount = flowTypeAccounts.data?.data?.find((account) => account.accountType === 8);
    if (!recyclingAccount) {
      return null;
    }

    return {
      accountId: recyclingAccount.id,
      accountName: recyclingAccount.name,
      accountType: recyclingAccount.accountType,
      physicalRoutingDefault: recyclingAccount.physicalRoutingDefault ?? PhysicalRouting.None,
    };
  }, [flowTypeAccounts.data?.data]);

  const handleOpenPriceOptions = useCallback((row: CategoryWithParts) => {
    const pricingData = bestMatrixPriceByCategoryId.get(row.category.id);
    if (!pricingData || pricingData.options.length <= 1) {
      return;
    }

    setSelectedPriceOptions({
      categoryId: row.category.id,
      categoryName: row.category.name,
      bestOption: pricingData.bestOption,
      otherOptions: pricingData.options.slice(1),
    });
    setPriceOptionsModalOpen(true);
  }, [bestMatrixPriceByCategoryId]);

  const handleSelectModalPrice = useCallback((option: IBestPriceOption) => {
    if (!selectedPriceOptions) {
      return;
    }

    handleSetTableData(selectedPriceOptions.categoryId, option.price, undefined, undefined, {
      accountId: option.accountId,
      accountName: option.accountName,
      accountType: option.accountType,
      physicalRoutingDefault: option.physicalRoutingDefault,
    });
    setPriceOptionsModalOpen(false);
  }, [handleSetTableData, selectedPriceOptions]);

  const handleClearSelectedAccount = useCallback((rowId: string) => {
    handleSetTableData(rowId, undefined, undefined, undefined, undefined, true);
  }, [handleSetTableData]);

  // When the TME feature is enabled, automatically select the best price for
  // every category: RS if it beats the matrix, otherwise the best matrix price,
  // falling back to the recycling account when no price exists. Only applies to
  // assembly tables (autoSelect) — non-assembly/manual parts are left untouched.
  //
  // RS prices arrive asynchronously (after the RS lookup completes), so this
  // can't be a one-shot run. We remember what we last auto-applied to each row
  // and re-evaluate as data loads, upgrading to RS once it's available — but
  // only while the row still matches what we applied (i.e. the user hasn't
  // manually changed it).
  const autoSelectedVehicleRef = useRef<string | null>(null);
  const autoSelectedIntentsRef = useRef<Record<string, AutoSelectIntent>>({});
  useEffect(() => {
    if (!showBestPrice || !autoSelect) return;
    if (vehiclePricingMatrix.isLoading || flowTypeAccounts.isLoading) return;
    if (!tableData.length) return;

    // Forget remembered intents when we switch vehicles.
    if (autoSelectedVehicleRef.current !== (vehicle?.id ?? null)) {
      autoSelectedVehicleRef.current = vehicle?.id ?? null;
      autoSelectedIntentsRef.current = {};
    }

    tableData.forEach((row) => {
      // Removed parts (kept visible only because they belong to an assembly)
      // must never be auto-selected.
      if (row.isRemoved) return;

      const rowId = row.category.id;

      const bestOption = bestMatrixPriceByCategoryId.get(rowId)?.bestOption;
      const rsPrice = row.category.rsPrice ?? null;
      const rsIsBest = rsPrice != null && rsPrice > (bestOption?.price ?? 0);

      // Work out what we'd ideally auto-select for this row right now.
      let desired: AutoSelectIntent | null = null;
      if (rsIsBest) {
        desired = { kind: "rs", price: rsPrice };
      } else if (bestOption) {
        desired = { kind: "matrix", price: bestOption.price, accountId: bestOption.accountId };
      } else if (firstRecyclingAccount) {
        desired = { kind: "recycling", accountId: firstRecyclingAccount.accountId };
      }
      if (!desired) return;

      const prevIntent = autoSelectedIntentsRef.current[rowId];

      if (!prevIntent) {
        // Never auto-selected: only act if the user hasn't set anything yet.
        if ((row.price && row.price > 0) || row.accountId) return;
      } else {
        // Already auto-selected once: only re-apply if the row still matches
        // what we last applied (the user hasn't touched it since).
        const unchanged =
          prevIntent.kind === "rs"
            ? row.isCore === true && row.price === prevIntent.price && !row.accountId
            : prevIntent.kind === "matrix"
              ? row.accountId === prevIntent.accountId && row.price === prevIntent.price
              : row.accountId === prevIntent.accountId;
        if (!unchanged) return;

        // Skip if the desired selection hasn't actually changed.
        const sameAsBefore =
          desired.kind === prevIntent.kind &&
          (desired.kind === "rs"
            ? desired.price === (prevIntent as { price: number }).price
            : desired.kind === "matrix"
              ? desired.price === (prevIntent as { price: number; accountId: string }).price &&
              desired.accountId === (prevIntent as { accountId: string }).accountId
              : desired.accountId === (prevIntent as { accountId: string }).accountId);
        if (sameAsBefore) return;
      }

      autoSelectedIntentsRef.current[rowId] = desired;

      if (desired.kind === "rs") {
        // RS wins: select it as core/RS and clear any matrix account.
        handleSetTableData(rowId, desired.price, undefined, true, undefined, true);
      } else if (desired.kind === "matrix" && bestOption) {
        handleSetTableData(rowId, bestOption.price, undefined, undefined, {
          accountId: bestOption.accountId,
          accountName: bestOption.accountName,
          accountType: bestOption.accountType,
          physicalRoutingDefault: bestOption.physicalRoutingDefault,
        });
      } else if (firstRecyclingAccount) {
        handleSetTableData(rowId, undefined, undefined, undefined, {
          accountId: firstRecyclingAccount.accountId,
          accountName: firstRecyclingAccount.accountName,
          accountType: firstRecyclingAccount.accountType,
          physicalRoutingDefault: firstRecyclingAccount.physicalRoutingDefault,
        });
      }
    });
  }, [
    showBestPrice,
    autoSelect,
    vehiclePricingMatrix.isLoading,
    flowTypeAccounts.isLoading,
    tableData,
    bestMatrixPriceByCategoryId,
    firstRecyclingAccount,
    handleSetTableData,
    vehicle?.id,
  ]);

  const baseCols = useMemo(() => [
    {
      id: 'dropdown',
      cell: ({ row }: any) => {
        const categoryId = row.original.category.id;
        const isExpanded = row.getIsExpanded();

        return (
          <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}
              style={{ transform: `rotate(${!isExpanded ? "0" : "-270"}deg)` }}
              className="p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    colHelper.accessor("category.name", {
      id: "category.name",
      header: "Name",
      cell: (info) =>
        <div className="flex gap-x-2 items-center justify-between">
          <div className="flex items-center gap-x-2">
            <p>{info.getValue()}</p>

          </div>
          <div className="flex items-center gap-x-1">
            {info.row.original.isRemoved && (
              <span className="rounded-full border border-yellow-300 bg-yellow-100 px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight text-yellow-800">
                Added
              </span>
            )}
            <EbayLinkButton query={makeModelString + "+" + info.getValue()} />
          </div>
        </div>,
    }),
    colHelper.display({
      id: "lookupMpn",
      header: "MPN",
      cell: (info) => {
        const cat = info.row.original.category;

        const mpnContent = partProLoading ? (
          <LoadingPulse width="w-full" />
        ) : partProUnlocked ? (
          <div className="flex gap-x-2 items-center justify-between">
            <span>{cat.lookupPartNumber ?? "-"}</span>
            {cat.lookupPartNumber && <EbayLinkButton query={cat.lookupPartNumber} />}
          </div>
        ) : (
          <span><LockClosedIcon height={15} width={15} /></span>
        );

        const hasZone = !!cat.ucidZone;
        const hasUcid = !!cat.ucid;
        const showDiagram = !!vehicle?.umc;

        const handleDiagramClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          setDiagramCategory(cat);
          if (hasZone && hasUcid) {
            setDiagramMode({ type: "view", zoneId: cat.ucidZone, ucid: cat.ucid });
          } else if (hasZone) {
            setDiagramMode({ type: "findZone", zoneId: cat.ucidZone });
          } else {
            setDiagramMode({ type: "findAll" });
          }
          setDiagramOpen(true);
        };

        return (
          <div className="space-y-1">
            {mpnContent}
            {showDiagram && (
              <button
                onClick={handleDiagramClick}
                className={classNames(
                  "text-xs px-2 py-0.5 rounded whitespace-nowrap",
                  hasZone && hasUcid
                    ? "bg-primary-100 text-primary-700 hover:bg-primary-200"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                )}
              >
                {hasZone && hasUcid ? "View Diagram" : "Find Diagram"}
              </button>
            )}
          </div>
        );
      }
    }),
    colHelper.accessor(
      row => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.internalStock !== undefined) {
          return row.internalStock;
        }
        return row.parts?.filter(part => !part.soldDate).length ?? 0;
      },
      {
        id: "partsCount",
        header: "Stock",
        cell: (info) => {
          const partsCount = info.getValue() as number;
          const partProCount = 0; //don't have this data yet
          return (
            <div>
              <p>
                <span className="text-gray-600 text-sm">
                  Internal:{" "}
                </span>
                <span>
                  {partsCount > 0 ? partsCount : "-"}
                </span>
              </p>
              <p className="flex items-center gap-x-1">
                <span className="text-gray-600 text-sm">
                  Part Pro:{" "}
                </span>
                {partProLoading ? (
                  <span className="-mr-10">
                    <LoadingPulse width="w-10" />
                  </span>
                ) : partProUnlocked ? (
                  <span className="text-wilma-500 font-semibold">
                    {partProCount > 0 ? partProCount : "-"}
                  </span>
                ) : (
                  <span className=""><LockClosedIcon height={15} width={15} /></span>
                )}
              </p>
            </div>
          );
        }
      }),
    colHelper.accessor(
      row => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.totalSold !== undefined) {
          return row.totalSold;
        }
        return row.parts?.filter(x => x.soldDate)?.length ?? 0;
      },
      {
        id: "soldQuantity",
        header: "Total Previously Sold",
        cell: (info) => {
          const row = info.row.original;
          // Use aggregate field if available, otherwise compute from parts
          const lastSold = row.lastSoldDate ?? row.parts?.filter(x => x.soldDate)?.sort((a, b) => (a.soldDate! < b.soldDate! ? 1 : -1))[0]?.soldDate;
          return (
            <div>
              <p>{info.getValue() ?? "-"}</p>
              {lastSold && (<p className="text-gray-600 text-sm">Last Sold: {formatDateTime(lastSold, DateTime.DATE_MED)}</p>)}
            </div>
          )
        }
      }),
    colHelper.accessor("note",
      {
        id: "note",
        header: "Internal Note",
        enableSorting: false,
        cell: (info) => {
          const currentValue = info.getValue();
          const rowId = info.row.original.category.id;
          return (
            <EditableNoteCell
              rowId={rowId}
              value={currentValue ?? ""}
              handleSetTableData={handleSetTableData}
            />
          );
        }
      }),
    colHelper.accessor(
      (row) => {
        // Use aggregate field if available, otherwise compute from parts
        if (row.lastSoldPrice !== undefined && row.lastSoldPrice !== null) {
          return row.lastSoldPrice;
        }
        return row.parts?.find((part: IPart) => part.soldDate)?.price || 0;
      },
      {
        id: "lastSoldPriceButton",
        header: "Last Sold",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <LastSoldPriceButton
              parts={row.parts ?? []}
              onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, undefined, undefined, true)}
              lastSoldPrice={row.lastSoldPrice ?? row.category.lastSoldPrice}
              readOnly={hasTmeFeature}
            />
          );
        },
      }
    ),
    colHelper.accessor(
      (row) => {
        // Use aggregate field if available, otherwise compute from parts
        const basePrice = row.lastSoldPrice ?? (row.parts?.find((part: IPart) => part.soldDate)?.price || 0);
        return parseFloat((basePrice * 1.1).toFixed(2));
      },
      {
        id: "marketPriceButton",
        header: "Market",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <SmartPriceButton
              categoryWithParts={row}
              onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, undefined, undefined, true)}
            />
          );
        },
      }
    ),
    colHelper.accessor((row) => {
      const basePrice = row.category.lookupAveragePrice || 0;
      return parseFloat((basePrice).toFixed(2));
    },
      {
        id: "partProPriceButton",
        header: "PartPro",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <PartProPriceButton
              category={row.category}
              onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, undefined, undefined, true)}
              partProUnlocked={partProUnlocked}
              partProLoading={partProLoading}
            />
          );
        },
      }
    ),
    colHelper.accessor((row) => row.price, {
      id: "price",
      header: "Price",
      sortingFn: "auto",
      // Use our EditablePriceCell component
      cell: (info) => {
        const currentValue = info.getValue() as number;
        const row = info.row.original;
        const rowId = row.category.id;
        return (
          <EditablePriceCell
            rowId={rowId}
            value={currentValue}
            accountId={row.accountId}
            accountName={row.accountName}
            accountType={row.accountType}
            onClearAccount={handleClearSelectedAccount}
            handleSetTableData={handleSetTableData}
          />
        );
      },
    }),
    colHelper.display({
      id: "select",
      header: ({ table }) => (
        <div className="flex justify-center items-center flex-1 gap-x-2 p-1">
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
          <button
            type="button"
            onClick={() => { if (table.getState().rowSelection) table.resetRowSelection(); }}
            className={classNames(
              "p-1 transition duration-500 cursor-pointer border rounded-full",
              table.getSelectedRowModel().rows.length === 0
                ? "pointer-events-none border-gray-300 text-gray-300"
                : "border-primary-700 bg-primary-600 hover:bg-primary-700 text-white"
            )}
            title="Clear selected categories"
          >
            <XMarkSolidIcon className="h-4 w-4" />
          </button>
        </div>
      ),
      cell: ({ row }) => {
        const rowId = row.original.category.id;

        return (
          <div className="flex justify-center items-center flex-1 gap-x-2 p-1" onClick={(e) => e.stopPropagation()}>
            <IndeterminateCheckbox
              {...{
                id: rowId,
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        );
      }
    }),
  ], [handleClearSelectedAccount, handleSetTableData, makeModelString, partProLoading, partProUnlocked, hasTmeFeature]);

  // TME-only column for quickly assigning the recycling account to a category.
  const recyclingCol = useMemo(() => [
    colHelper.display({
      id: "recyclingButton",
      header: "Recycling",
      cell: ({ row }) => {
        const rowId = row.original.category.id;

        if (!firstRecyclingAccount) return <span></span>;

        return (
          <div className="flex justify-center items-center flex-1 p-1" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => handleSetTableData(rowId, undefined, undefined, undefined, {
                accountId: firstRecyclingAccount.accountId,
                accountName: firstRecyclingAccount.accountName,
                accountType: firstRecyclingAccount.accountType,
                physicalRoutingDefault: firstRecyclingAccount.physicalRoutingDefault,
              })}
              className="rounded border border-purple-300 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700 hover:bg-purple-100"
              title={`Select ${firstRecyclingAccount.accountName}`}
            >
              Recycling
            </button>
          </div>
        );
      }
    })
  ], [firstRecyclingAccount, handleSetTableData]);

  // TME-only column for choosing where a part is physically routed. Defaults from
  // the selected account's PhysicalRoutingDefault but can be overridden per part.
  const routingCol = useMemo(() => [
    colHelper.display({
      id: "physicalRouting",
      header: "Routing",
      cell: ({ row }) => {
        const rowId = row.original.category.id;
        const value = row.original.physicalRouting ?? PhysicalRouting.None;

        return (
          <div className="flex justify-center items-center flex-1 p-1" onClick={(e) => e.stopPropagation()}>
            <select
              value={value}
              onChange={(e) =>
                handleSetTableData(rowId, undefined, undefined, undefined, undefined, false, Number(e.target.value) as PhysicalRouting)
              }
              className="border p-1 text-sm"
            >
              {physicalRoutingOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
      }
    })
  ], [handleSetTableData]);

  const rsCols = useMemo(() => [
    colHelper.accessor((row) => {
      const basePrice = row.category.lookupAveragePrice || 0;
      return parseFloat((basePrice).toFixed(2));
    },
      {
        id: "rsPriceButton",
        header: "RS",
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.category.id;

          return (
            <RsPriceButton
              category={row.category}
              onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, true, undefined, true)}
            />
          );
        },
      }
    )
  ], [handleSetTableData]);

  useEffect(() => {
    if (singleSetting.data?.data && singleSetting.data.data.value != "" && singleSetting.data.data.value != null) {
      setHasRsKey(true);
    } else {
      setHasRsKey(false);
    }
  }, [singleSetting.data, singleSetting.isLoading]);

  const accountCol = useMemo(() => [
    colHelper.display({
      id: "accountPriceButton",
      header: "Flow",
      cell: (info) => {
        const row = info.row.original;
        const rowId = row.category.id;
        const pricingData = bestMatrixPriceByCategoryId.get(rowId);
        const bestPricing = pricingData?.bestOption;
        const rsPrice = row.category.rsPrice ?? null;

        // Under TME the RS column is hidden, so surface RS in the Best column
        // when it beats the best matrix price (or when there's no matrix price).
        const rsIsBest = hasTmeFeature && rsPrice != null && rsPrice > (bestPricing?.price ?? 0);

        if (!bestPricing && !rsIsBest) {
          return <span></span>;
        }

        const hasOtherOptions = (pricingData?.options.length ?? 0) > 1;

        return (
          <div className="flex flex-col items-center gap-y-1">
            <div className="flex items-center gap-x-1">
              {rsIsBest ? (
                <RsPriceButton
                  category={row.category}
                  onPriceSelect={(price) => handleSetTableData(rowId, price, undefined, true, undefined, true)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    handleSetTableData(rowId, bestPricing!.price, undefined, undefined, {
                      accountId: bestPricing!.accountId,
                      accountName: bestPricing!.accountName,
                      accountType: bestPricing!.accountType,
                      physicalRoutingDefault: bestPricing!.physicalRoutingDefault,
                    })
                  }
                  className="border text-white shadow rounded-md p-1 px-2 bg-emerald-600 hover:bg-emerald-500 w-20 text-center"
                >
                  <span className="font-bold">{PriceFormat(bestPricing!.price)}</span>
                </button>
              )}
              {hasOtherOptions && (
                <button
                  type="button"
                  onClick={() => handleOpenPriceOptions(row)}
                  className="rounded-md border border-gray-300 bg-white p-1 text-gray-600 hover:bg-gray-50"
                  title="View other account prices"
                >
                  <EllipsisVerticalIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            {rsIsBest ? (
              <span
                className="max-w-[86px] truncate rounded-sm border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-normal leading-tight text-[#69141E]"
                title="RS Automotive Price"
              >
                RS Automotive
              </span>
            ) : (
              <span
                className={classNames(
                  "max-w-[86px] truncate rounded-sm border px-1.5 py-0.5 text-[10px] font-normal leading-tight",
                  accountTypePillClassMap[bestPricing!.accountType] ?? "border-gray-200 bg-gray-100 text-gray-500"
                )}
                title={bestPricing!.accountName}
              >
                {bestPricing!.accountName}
              </span>
            )}
          </div>
        );
      },
    })
  ], [bestMatrixPriceByCategoryId, handleOpenPriceOptions, handleSetTableData, hasTmeFeature]);

  // slice rsCols/best column into base columns after source price columns
  const cols = useMemo(() => {
    let newCols = [...baseCols];

    // TME hides the Market and PartPro source-price columns.
    if (hasTmeFeature) {
      newCols = newCols.filter(col => col.id !== "marketPriceButton" && col.id !== "partProPriceButton");
    }

    // RS column is also hidden under TME.
    if (hasRsKey && !hasTmeFeature) {
      const partProIndex = newCols.findIndex(col => col.id === "partProPriceButton");
      if (partProIndex !== -1) {
        newCols.splice(partProIndex + 1, 0, ...rsCols);
      }
    }

    if (showBestPrice) {
      const priceIndex = newCols.findIndex(col => col.id === "price");
      if (priceIndex !== -1) {
        newCols.splice(priceIndex, 0, ...accountCol);
      }
    }

    // TME-only Recycling quick-action column, placed right after the Flow column.
    if (hasTmeFeature) {
      const flowIndex = newCols.findIndex(col => col.id === "accountPriceButton");
      if (flowIndex !== -1) {
        newCols.splice(flowIndex + 1, 0, ...recyclingCol);
      } else {
        const selectIndex = newCols.findIndex(col => col.id === "select");
        if (selectIndex !== -1) {
          newCols.splice(selectIndex, 0, ...recyclingCol);
        } else {
          newCols.push(...recyclingCol);
        }
      }
    }

    // TME-only Routing dropdown, placed right after the Internal Note column.
    if (hasTmeFeature) {
      const noteIndex = newCols.findIndex(col => col.id === "note");
      if (noteIndex !== -1) {
        newCols.splice(noteIndex + 1, 0, ...routingCol);
      } else {
        newCols.push(...routingCol);
      }
    }

    return newCols;
  }, [hasRsKey, baseCols, rsCols, showBestPrice, accountCol, recyclingCol, routingCol, hasTmeFeature]);


  const table = useReactTable({
    data: filteredTableData,
    columns: cols,
    getRowId: (row) => row.category.id,
    state: {
      sorting: sorting,
      rowSelection: rowSelection,
      expanded: expanded
    },
    enableRowSelection: true,
    onRowSelectionChange: (updaterOrState) => {
      // If it's a function, call it with the current state to get newState.
      const newState: Record<string, boolean> =
        typeof updaterOrState === "function"
          ? updaterOrState(rowSelection)
          : updaterOrState;

      const newlySelected: CategoryWithParts[] = [];
      for (const [rowId, isSel] of Object.entries(newState)) {
        if (isSel) {
          const found = tableData.find((c) => c.category.id === rowId);
          if (found) newlySelected.push(found);
        }
      }
      setSelectedCategoriesRef.current(newlySelected);
    },
    onExpandedChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        setExpanded(prev => {
          const newValue = updaterOrValue(prev);
          // Ensure only one row is expanded at a time
          if (typeof newValue === 'boolean') return {};
          const trueKeys = Object.keys(newValue).filter(key => newValue[key]);
          if (trueKeys.length > 1) {
            // Keep only the last one (most recently expanded)
            const lastKey = trueKeys[trueKeys.length - 1];
            return { [lastKey]: true };
          }
          return newValue;
        });
      } else {
        if (typeof updaterOrValue === 'boolean') {
          setExpanded({});
        } else {
          // Ensure only one row is expanded
          const trueKeys = Object.keys(updaterOrValue).filter(key => updaterOrValue[key]);
          if (trueKeys.length > 1) {
            const lastKey = trueKeys[trueKeys.length - 1];
            setExpanded({ [lastKey]: true });
          } else {
            setExpanded(updaterOrValue);
          }
        }
      }
    },
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    setTableData(prev => {
      const prevById = new Map(prev.map(c => [c.category.id, c]));

      return categories.map(incoming => {
        const local = prevById.get(incoming.category.id);
        if (!local) return incoming;

        return {
          ...incoming,
          // Preserve user-entered local fields
          ...(local.price != null ? { price: local.price } : {}),
          ...(local.note != null ? { note: local.note } : {}),
          ...(local.isCore != null ? { isCore: local.isCore } : {}),
          ...(local.accountId !== undefined ? { accountId: local.accountId } : {}),
          ...(local.accountName !== undefined ? { accountName: local.accountName } : {}),
          ...(local.accountType !== undefined ? { accountType: local.accountType } : {}),
          ...(local.physicalRouting !== undefined ? { physicalRouting: local.physicalRouting } : {}),
          category: {
            ...incoming.category,
          },
        };
      });
    });
  }, [categories]);

  return (
    <>
      <Modal open={priceOptionsModalOpen} setOpen={setPriceOptionsModalOpen} width="max-w-xl">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Other Account Prices</h3>
            <p className="text-sm text-gray-600">
              {selectedPriceOptions?.categoryName ?? ""}
            </p>
          </div>

          {!!selectedPriceOptions && (
            <div className="space-y-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Best Price</div>
                <button
                  type="button"
                  onClick={() => handleSelectModalPrice(selectedPriceOptions.bestOption)}
                  className="w-full flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 hover:bg-emerald-100"
                >
                  <span className="font-semibold text-emerald-700">{PriceFormat(selectedPriceOptions.bestOption.price)}</span>
                  <span
                    className={classNames(
                      "truncate rounded-sm border px-1.5 py-0.5 text-[10px] font-normal leading-tight",
                      accountTypePillClassMap[selectedPriceOptions.bestOption.accountType] ?? "border-gray-200 bg-gray-100 text-gray-500"
                    )}
                  >
                    {selectedPriceOptions.bestOption.accountName} ({accountTypeLabelMap[selectedPriceOptions.bestOption.accountType] ?? `Type ${selectedPriceOptions.bestOption.accountType}`})
                  </span>
                </button>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Other Prices</div>
                <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-200">
                  {selectedPriceOptions.otherOptions.map((option) => (
                    <button
                      key={`${option.accountId}-${option.price}`}
                      type="button"
                      onClick={() => handleSelectModalPrice(option)}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-800">{PriceFormat(option.price)}</span>
                      <span
                        className={classNames(
                          "truncate rounded-sm border px-1.5 py-0.5 text-[10px] font-normal leading-tight",
                          accountTypePillClassMap[option.accountType] ?? "border-gray-200 bg-gray-100 text-gray-500"
                        )}
                      >
                        {option.accountName} ({accountTypeLabelMap[option.accountType] ?? `Type ${option.accountType}`})
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <div className="mt-4 bg-white overflow-auto space-y-4">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">

          {/* Table headings - using nth child to make the name column the same width */}
          {/* Also using first and last to make sure the dropdown and select boxes are the same width */}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800 [&>*:nth-child(2)]:w-48" >
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="flex first:w-16 last:w-16 py-1 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>

                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody>
            {table.getRowModel().rows.map((row) => {
              let rowCells = row.getVisibleCells();
              let isRemoved = row.original.isRemoved === true;
              let noPriceSelected = row.getIsSelected() === true && (!row.original.price || row.original.price <= 0) && !row.original.accountId;

              return (
                <Fragment key={row.id}>
                  <tr
                    key={row.id}
                    // onClick={() => setLocation(row.original)}
                    className={classNames(isRemoved ? "bg-yellow-100" : noPriceSelected ? "bg-red-200" : "", `hover:bg-gray-100`)}
                  >
                    {rowCells.map((cell) => (
                      <td
                        key={cell.id}
                        className={classNames(
                          "px-3 border text-sm",
                          cell.column.id === "category.name" ? "w-72" : ""
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr>
                      {/* 2nd row is a custom 1 cell row */}
                      <td colSpan={row.getVisibleCells().length}>
                        <LeadPartsCategoriesTableSubRow
                          categoryWithParts={row.original}
                          vehicleId={vehicle.id}
                          vehicleYear={vehicle.dvlaYearOfManufacture ?? null}
                          vehicleVisibilityDate={vehicle.visibilityDate ?? null}
                          vehicleTerminateDate={vehicle.terminateDate ?? null}
                          vehicleModelSeries={vehicle.modelSeries ?? null}
                          leadPartsTableFilters={leadPartsTableFilters}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>

        </table>
      </div>

      {vehicle?.umc && diagramCategory && (
        <PartDiagramModal
          open={diagramOpen}
          setOpen={setDiagramOpen}
          umc={vehicle.umc}
          category={diagramCategory}
          mode={diagramMode}
        />
      )}
    </>
  );
}
